import { Sheet, SheetContent, SheetHeader, SheetTitle } from "~/atoms/sheet";
import { useDocument } from "~/api/v2/useDocument";
import { CenterSpinner } from "~/atoms/spinner";
import { DocumentMetadata } from "./DocumentMetadata";
import { useNavigate } from "react-router";
import { Button } from "~/atoms/button";
import { useState } from "react";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";
import { useTranslation } from "react-i18next";
import NoResult from "~/molecules/NoResult";
import { type DocumentSummaryVersion } from "~/api/types";

interface Props {
  documentId: number;
  documentVersion: DocumentSummaryVersion;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSave: () => void;
}

export function DocumentMetadataSheet({
  documentId,
  documentVersion,
  open,
  setOpen,
  onSave,
}: Props) {
  const { t } = useTranslation();
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCloseAlert, setShowCloseAlert] = useState(false);
  const {
    data: document,
    error,
    isLoading,
    mutate: mutateDocument,
  } = useDocument(documentId);

  const handleSave = () => {
    void mutateDocument();
    onSave();
  };

  const navigate = useNavigate();

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      return;
    }

    if (isEditingMetadata && hasUnsavedChanges) {
      setShowCloseAlert(true);
      return;
    }

    setOpen(false);
  };

  const handleLeaveSheet = () => {
    setShowCloseAlert(false);
    setOpen(false);
  };

  return (
    <>
      <UnsavedChangesAlert
        hasUnsavedChanges={false}
        manualOpen={showCloseAlert}
        onManualOpenChange={setShowCloseAlert}
        onManualLeave={handleLeaveSheet}
        title={t(($) => $.commons.unsavedChanges.metadataSheet.title)}
        description={t(
          ($) => $.commons.unsavedChanges.metadataSheet.description,
        )}
        cancelLabel={t(($) => $.commons.unsavedChanges.continueEditing)}
        confirmLabel={t(($) => $.commons.unsavedChanges.discardChanges)}
      />
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle asChild>
              <Button
                variant="link"
                onClick={() => {
                  void navigate(
                    `/documents/${documentId}?versionId=${documentVersion.id}&tab=metadata`,
                  );
                }}
                title={documentVersion.title}
                className="line-clamp-4 h-auto p-0 text-left text-lg font-semibold wrap-break-word whitespace-normal"
              >
                {documentVersion.title}
              </Button>
            </SheetTitle>
          </SheetHeader>

          {isLoading ? (
            <CenterSpinner />
          ) : error || !document ? (
            <NoResult message={t(($) => $.commons.errors.noDocument)} />
          ) : (
            <div className="px-4">
              <DocumentMetadata
                document={document}
                documentVersionId={documentVersion.id}
                onSave={handleSave}
                onEditModeChange={setIsEditingMetadata}
                onUnsavedChangesChange={setHasUnsavedChanges}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
